import type { FetchArgType } from "openapi-typescript-fetch";
import type {
    GetListParams,
    GetListResult,
    GetManyReferenceParams,
    GetManyReferenceResult,
    UpdateParams,
    UpdateManyParams,
    CreateParams,
    DeleteManyParams,
    DeleteManyResult,
    UpdateResult,
    GetManyResult,
    CreateResult
} from "react-admin";
import { isEmpty } from "lodash";

import { fetcher } from "../../data";
import { isDefined } from "../../utils/typedLoDashUtils";
import { assertIsDefined } from "../../utils/assertions";
import type { BaseFilters } from "../../data/BaseFilters";
import { NotImplementedError } from "../../utils/NotImplementedError";
import type { ErrorDetails, LangParams, MpmDataProvider, MpmDataProviderOperationName } from "../../data";

import type { AdminUserDTO } from "./AdminUserDTO";
import type { AdminUserRecord } from "./AdminUserRecord";

const findAll = fetcher.path("/api/user/user/findAll").method("post").create();
const createOne = fetcher.path("/api/user/user/").method("post").create();
const getOne = fetcher.path("/api/user/user/{uuid}").method("get").create();
const updateOne = fetcher.path("/api/user/user/{uuid}").method("put").create();
const deleteOne = fetcher.path("/api/user/user/{uuid}").method("delete").create();
const getAllByUuids = fetcher.path("/api/user/user/allByUuids").method("get").create();

/**
 * Parses the DTO from backend to record for frontend
 * @param dto
 * @returns UserRecord
 */

function mapDTOToRecord(dto: AdminUserDTO): AdminUserRecord {
    // DTO from backend
    const { id = 0, uuid = "", username = "", firstName = "", lastName = "", lastLogin = "", email = "", enabled = false } = dto;

    // Record for front-end
    return {
        shortId: id,
        username,
        id: uuid,
        email,
        firstName,
        lastName,
        lastLogin: new Date(lastLogin),
        enabled
    };
}

/**
 * Parses the record from frontend to DTO for backend
 * @param record
 * @returns UserDTO
 */

function mapRecordToDTO(record: AdminUserRecord): AdminUserDTO {
    // Record from front-end
    const {
        id = "",
        shortId = 0,
        username = "",
        firstName = "",
        lastName = "",
        email = "",
        newPassword,
        confirmedPassword,
        lastLogin,
        enabled
    } = record;

    // DTO for back-end
    return {
        id: shortId,
        uuid: id as string,
        username: username ?? "",
        email,
        firstName: firstName ?? "",
        lastName: lastName ?? "",
        lastLogin: lastLogin?.valueOf().toString() ?? "",
        newPassword,
        confirmedPassword,
        enabled
    };
}

interface UserFilters extends BaseFilters {
    q?: string;
    email: string;
}

async function getList(params: Readonly<GetListParams>): Promise<GetListResult<AdminUserRecord>> {
    const { q: searchTerms, ...filters } = params.filter as UserFilters;

    let parameters: FetchArgType<typeof findAll> = {
        filter: filters,
        pageable: {
            page: params.pagination.page - 1,
            size: params.pagination.perPage,
            sort: isEmpty(params.sort.field) ? [] : [`${params.sort.field},${params.sort.order}`]
        }
    } as const;

    if (isDefined(searchTerms)) {
        parameters = {
            ...parameters,
            filter: { ...parameters.filter, searchTerms }
        };
    }

    const { data } = await findAll(parameters);
    const dataPage = data.content;
    assertIsDefined(dataPage);
    const total = dataPage.totalElements;
    assertIsDefined(total);
    assertIsDefined(dataPage.content);

    return {
        data: dataPage.content.map((content) => mapDTOToRecord({ id: content.id ?? 0, ...content })),
        total
    };
}

async function _getOne(params: LangParams | (number | string)): Promise<AdminUserRecord> {
    const {
        data: { content: userDTO }
    } = await getOne({
        uuid: params as string
    });
    assertIsDefined(userDTO);

    return mapDTOToRecord({ id: userDTO.id ?? 0, ...userDTO });
}

async function getMany(uuids: readonly (number | string)[]): Promise<GetManyResult<AdminUserRecord>> {
    const {
        data: { content: userDTO }
    } = await getAllByUuids({
        uuids: uuids as string[]
    });
    assertIsDefined(userDTO);

    return { data: await Promise.all(userDTO.map((dto) => mapDTOToRecord({ id: dto.id ?? 0, ...dto }))) };
}

async function getManyReference(_params: Readonly<GetManyReferenceParams>): Promise<GetManyReferenceResult<AdminUserRecord>> {
    return Promise.reject(new NotImplementedError("getManyReference"));
}

async function update(params: Readonly<UpdateParams<AdminUserRecord>>): Promise<UpdateResult<AdminUserRecord>> {
    const payload = mapRecordToDTO(params.data as AdminUserRecord);
    const parameters = { uuid: payload.uuid ?? "", ...payload };
    delete (parameters as any)["lastLogin"];
    const { data } = await updateOne(parameters);
    const userDTO = data.content;
    assertIsDefined(userDTO);

    return { data: mapDTOToRecord({ id: parameters.id, ...userDTO }) };
}

async function updateMany(_params: Readonly<UpdateManyParams>): Promise<string[]> {
    return Promise.reject(new NotImplementedError("updateMany"));
}

async function create(params: Readonly<CreateParams>): Promise<CreateResult<AdminUserRecord>> {
    const payload = mapRecordToDTO(params.data as any);

    assertIsDefined(payload.username);
    const parameters = { ...payload };
    delete (parameters as any)["id"];
    delete (parameters as any)["uuid"];
    const { data } = await createOne(parameters);
    const contentOriginDTO = data.content;

    assertIsDefined(contentOriginDTO);

    return { data: mapDTOToRecord({ id: contentOriginDTO.id as number, ...contentOriginDTO }) };
}

// eslint-disable-next-line no-underscore-dangle
async function _delete(uuid: number | string): Promise<AdminUserRecord> {
    const {
        data: { content: isDeleted }
    } = await deleteOne({
        uuid: uuid as string
    });
    assertIsDefined(isDeleted);

    return {} as AdminUserRecord;
}

async function deleteMany(params: Readonly<DeleteManyParams>): Promise<DeleteManyResult> {
    await Promise.all(params.ids.map((id: string) => _delete(id)));
    return {
        data: params.ids.map((id: string) => {
            id;
        })
    };
}

function getErrorDetails(_error: unknown, _operation: MpmDataProviderOperationName<AdminUserRecord>): ErrorDetails | undefined {
    // eslint-disable-next-line no-undefined
    return undefined;
}

export const AdminUserDataProvider: MpmDataProvider<AdminUserRecord> = {
    create,
    delete: _delete,
    deleteMany,
    getErrorDetails,
    getList,
    getMany,
    getManyReference,
    getOne: _getOne,
    update,
    updateMany
};
